/**
 * Opt-in variable binding for SchemaForm / FlowV2 essentials.
 *
 * **Use variable** (`AllowVariable`): model stores `{Name}`; essential UI hides braces.
 * **Save / output target** (`AllowVariableTarget`): model stores bare `Name` (ReturnVar / Out args).
 *
 * Custom Form authors (UserControl under Flow essentials): inject
 * `variableBindingContext` or wrap fields with `<VariableBindableField>`.
 * Use `formatBinding` for interpolate-style fields; `formatVariableTarget` for Output targets.
 *
 * Per-field gates (property-level and/or `formatData`):
 * - `AllowVariable` — `{}` affordance for inbound literals
 * - `AllowVariableTarget` — assign CTA for Output / ReturnVar (no free-text in Essentials)
 *
 * Already-bound values still show the pill + clear so users can recover.
 */

/** Inject / provide key (ancestor overlay/dock/dialog or SchemaForm). */
export const VARIABLE_BINDING_INJECT_KEY = 'variableBindingContext';

const SIMPLE_BINDING_RE = /^\{([A-Za-z_][\w]*)\}$/;
/** Bare identifier for Output / ReturnVar save targets (no braces). */
const VARIABLE_TARGET_RE = /^[A-Za-z_][\w]*$/;

export const USE_VARIABLE_TOOLTIP = 'Use a variable instead of a fixed value';
export const USING_VARIABLE_TOOLTIP = 'Using a variable — clear to use a fixed value';
export const ASSIGN_VARIABLE_TOOLTIP = 'Assign output to a variable';
export const ASSIGNED_VARIABLE_TOOLTIP = 'Output saved to this variable — clear to reassign';
export const EXPRESSION_CHIP_TOOLTIP = 'This field uses an expression — clear to enter a fixed value';

/** Full-string numeric literal (optional sign, decimals, scientific). */
const NUMBER_LITERAL_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const INTEGER_LITERAL_RE = /^[+-]?\d+$/;

/**
 * Whether `value` can be shown in an Essentials typed control for `schema`.
 * Empty/null is always representable. Simple `{Name}` bindings are not literals
 * (callers should prefer the variable pill via isSimpleVariableBinding).
 *
 * Free-text string fields treat any non-binding value as representable.
 * Constrained selects/enums (no AllowCustomInput) require membership in options.
 *
 * @param {unknown} value
 * @param {object|null|undefined} schema  SchemaForm leaf schema (`element.schema`)
 * @param {{ enumValues?: unknown[], allowCustomInput?: boolean }} [options]
 * @returns {boolean}
 */
export function isEssentialsLiteral(value, schema, options = {}) {
    if (value == null || value === '')
        return true;

    if (isSimpleVariableBinding(value))
        return false;

    // Output / ReturnVar: no free-text literal UI; identifier → pill, else expression chip.
    if (schemaAllowsVariableTarget(schema))
        return false;

    const type = schema && typeof schema === 'object' ? schema.type : undefined;
    // Array controls cannot show a string expression as list items. Checked before
    // enum/titleMap so a matching item name is not treated as the list itself.
    if (type === 'array')
        return Array.isArray(value);

    const allowCustom = options.allowCustomInput === true
        || !!(schema && schema.formatData && schema.formatData.AllowCustomInput);

    const enumValues = resolveEnumValues(schema, options.enumValues);
    if (Array.isArray(enumValues) && enumValues.length > 0 && !allowCustom)
        return enumValues.some(v => v == value);

    if (type === 'number' || type === 'integer')
        return isNumericLiteral(value, type === 'integer');

    if (type === 'boolean')
        return isBooleanLiteral(value);

    // string / missing type / object: free-text or not Essentials-typed
    return true;
}

/**
 * True when Essentials should show an expression chip instead of the typed control.
 * Simple `{Name}` bindings and bare Output targets are excluded (variable pill handles those).
 *
 * @param {unknown} value
 * @param {object|null|undefined} schema
 * @param {{ enumValues?: unknown[], allowCustomInput?: boolean }} [options]
 * @returns {boolean}
 */
export function needsEssentialsExpressionChip(value, schema, options = {}) {
    if (value == null || value === '')
        return false;
    if (isSimpleVariableBinding(value))
        return false;
    if (schemaAllowsVariableTarget(schema) && isSimpleVariableTarget(value))
        return false;
    return !isEssentialsLiteral(value, schema, options);
}

/**
 * Expression chip only when the FlowV2 variable-binding host is on.
 * Per-field AllowVariable is not required. Outside that host, a titleMap
 * miss or other non-literal stays a normal control.
 *
 * @param {boolean} hostEnabled
 * @param {unknown} value
 * @param {object|null|undefined} schema
 * @param {{ enumValues?: unknown[], allowCustomInput?: boolean }} [options]
 * @returns {boolean}
 */
export function showEssentialsExpressionChip(hostEnabled, value, schema, options = {}) {
    if (!hostEnabled)
        return false;
    return needsEssentialsExpressionChip(value, schema, options);
}

/**
 * Value written when Essentials clears an expression stored in an array field.
 * Uses the schema default when that default is already an array.
 * @param {object|null|undefined} schema
 * @returns {unknown[]}
 */
export function arrayExpressionRestoreValue(schema) {
    const fallback = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
        ? schema.default
        : undefined;
    return Array.isArray(fallback) ? fallback : [];
}

/**
 * @param {object|null|undefined} schema
 * @param {unknown[]|undefined} override
 * @returns {unknown[]|null}
 */
function resolveEnumValues(schema, override) {
    if (Array.isArray(override) && override.length > 0)
        return override;
    if (!schema || typeof schema !== 'object')
        return null;
    if (Array.isArray(schema.enum) && schema.enum.length > 0)
        return schema.enum;
    if (Array.isArray(schema.titleMap) && schema.titleMap.length > 0)
        return schema.titleMap.map(t => (t && typeof t === 'object' && 'value' in t ? t.value : t));
    return null;
}

/**
 * @param {unknown} value
 * @param {boolean} integerOnly
 * @returns {boolean}
 */
export function isNumericLiteral(value, integerOnly) {
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            return false;
        return integerOnly ? Number.isInteger(value) : true;
    }
    if (typeof value !== 'string')
        return false;
    const t = value.trim();
    if (!t)
        return true;
    if (integerOnly) {
        if (!INTEGER_LITERAL_RE.test(t))
            return false;
        return true;
    }
    if (!NUMBER_LITERAL_RE.test(t))
        return false;
    return Number.isFinite(Number(t));
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isBooleanLiteral(value) {
    if (typeof value === 'boolean')
        return true;
    if (typeof value !== 'string')
        return false;
    const t = value.trim().toLowerCase();
    return t === 'true' || t === 'false';
}

/**
 * Strict per-field opt-in for interpolate-style variable assist (`{Name}`).
 * @param {object|null|undefined} schema  SchemaForm leaf schema (`element.schema`)
 * @returns {boolean}
 */
function allowsVariableFlag(value) {
    return value === true || value === 'true';
}

export function schemaAllowsVariableBinding(schema) {
    if (!schema || typeof schema !== 'object')
        return false;
    if (allowsVariableFlag(schema.AllowVariable))
        return true;
    const fd = schema.formatData;
    if (fd && typeof fd === 'object' && allowsVariableFlag(fd.AllowVariable))
        return true;
    return false;
}

/**
 * Strict per-field opt-in for Output / ReturnVar save-target assist (bare `Name`).
 * @param {object|null|undefined} schema
 * @returns {boolean}
 */
export function schemaAllowsVariableTarget(schema) {
    if (!schema || typeof schema !== 'object')
        return false;
    if (schema.AllowVariableTarget === true)
        return true;
    const fd = schema.formatData;
    if (fd && typeof fd === 'object' && fd.AllowVariableTarget === true)
        return true;
    return false;
}

/**
 * Flow local type for a JSON Schema type when the field has no VariableType.
 * @param {unknown} typeName
 * @returns {string|undefined}
 */
function flowTypeFromJsonSchemaType(typeName) {
    const t = (typeName == null ? '' : String(typeName)).trim().toLowerCase();
    if (t === 'boolean' || t === 'bool')
        return 'bool';
    if (t === 'integer' || t === 'int')
        return 'int';
    if (t === 'number' || t === 'double' || t === 'float')
        return 'double';
    return undefined;
}

const INTEGER_FLOW_TYPES = new Set(['int', 'long', 'integer']);
const NUMBER_FLOW_TYPES = new Set(['float', 'double', 'number']);

/**
 * Flow numeric kind for a type name, or null when it is not numeric.
 * @param {unknown} typeName
 * @returns {'integer'|'number'|null}
 */
export function flowNumericKind(typeName) {
    const n = normalizeTypeName(typeName);
    if (INTEGER_FLOW_TYPES.has(n))
        return 'integer';
    if (NUMBER_FLOW_TYPES.has(n))
        return 'number';
    return null;
}

/**
 * String schema that Flow generated so Essentials can show a number box for literals
 * while Advanced stays a text expression field.
 * Ordinary `{ type: 'number' }` schemas are not this path.
 * @param {object|null|undefined} schema
 * @returns {boolean}
 */
export function isFlowExpressionNumericField(schema) {
    if (!schema || typeof schema !== 'object' || schema.type !== 'string')
        return false;
    if (!schemaAllowsVariableBinding(schema))
        return false;
    if (flowNumericKind(schemaVariableTypeHint(schema)) == null)
        return false;
    const format = schema.format == null ? '' : String(schema.format).toLowerCase();
    if (format === 'lookuplist')
        return false;
    if (Array.isArray(schema.enum) && schema.enum.length > 0)
        return false;
    if (Array.isArray(schema.titleMap) && schema.titleMap.length > 0)
        return false;
    return true;
}

/**
 * Essentials control for an expression-capable numeric field.
 * @param {object|null|undefined} schema
 * @param {unknown} value
 * @returns {'number'|'text'|null}
 */
export function flowExpressionNumericControl(schema, value) {
    if (!isFlowExpressionNumericField(schema))
        return null;
    const integerOnly = flowNumericKind(schemaVariableTypeHint(schema)) === 'integer';
    if (value == null || value === '' || isNumericLiteral(value, integerOnly))
        return 'number';
    return 'text';
}

/**
 * Type hint for createVariable (`VariableType`, else the field's JSON Schema type).
 * @param {object|null|undefined} schema
 * @returns {string|undefined}
 */
export function schemaVariableTypeHint(schema) {
    if (!schema || typeof schema !== 'object')
        return undefined;
    if (typeof schema.VariableType === 'string' && schema.VariableType.trim())
        return schema.VariableType.trim();
    const fd = schema.formatData;
    if (fd && typeof fd === 'object' && typeof fd.VariableType === 'string' && fd.VariableType.trim())
        return fd.VariableType.trim();
    if (typeof schema.type === 'string')
        return flowTypeFromJsonSchemaType(schema.type);
    return undefined;
}

/**
 * @param {unknown} value
 * @returns {string|null} variable name without braces, or null if not a simple binding
 */
export function parseBinding(value) {
    if (value == null || typeof value !== 'string')
        return null;
    const m = SIMPLE_BINDING_RE.exec(value.trim());
    return m ? m[1] : null;
}

/**
 * @param {string} name
 * @returns {string}
 */
export function formatBinding(name) {
    const n = (name == null ? '' : String(name)).trim();
    if (!n)
        return '';
    return `{${n}}`;
}

/**
 * @param {unknown} value
 */
export function isSimpleVariableBinding(value) {
    return parseBinding(value) != null;
}

/**
 * Parse a bare Output / ReturnVar save target (no braces).
 * @param {unknown} value
 * @returns {string|null}
 */
export function parseVariableTarget(value) {
    if (value == null || typeof value !== 'string')
        return null;
    const t = value.trim();
    if (!t || !VARIABLE_TARGET_RE.test(t))
        return null;
    return t;
}

/**
 * Store a bare variable name for Output / ReturnVar.
 * @param {string} name
 * @returns {string}
 */
export function formatVariableTarget(name) {
    const n = (name == null ? '' : String(name)).trim();
    if (!n || !VARIABLE_TARGET_RE.test(n))
        return '';
    return n;
}

/**
 * @param {unknown} value
 */
export function isSimpleVariableTarget(value) {
    return parseVariableTarget(value) != null;
}

/**
 * Normalize a Flow type name for comparison (trim + lowercase).
 * @param {unknown} typeName
 * @returns {string}
 */
export function normalizeTypeName(typeName) {
    if (typeName == null)
        return '';
    return String(typeName).trim().toLowerCase();
}

/**
 * Whether expected type is string-like (use-variable allows any symbol via interpolation).
 * @param {unknown} typeName
 * @returns {boolean}
 */
export function isStringLikeType(typeName) {
    const n = normalizeTypeName(typeName);
    return !n || n === 'string';
}

/**
 * Client-side type compatibility for the Essentials variable picker.
 * @param {unknown} symbolType  local/parameter Type
 * @param {unknown} expectedType  schema VariableType
 * @param {{ mode?: 'use'|'target' }} [options]
 * @returns {boolean}
 */
export function typesCompatible(symbolType, expectedType, options = {}) {
    const mode = options.mode === 'target' ? 'target' : 'use';
    const expected = (expectedType == null ? '' : String(expectedType)).trim();

    if (mode === 'use' && isStringLikeType(expected))
        return true;

    if (!expected)
        return true;

    const left = normalizeTypeName(symbolType);
    const right = normalizeTypeName(expected);
    if (!left)
        return false;
    return left === right;
}

/**
 * Build picker symbol list for a binding mode.
 * @param {{ variables?: Array, parameters?: Array }} host
 * @param {'use'|'target'} mode
 * @returns {Array<{Name:string,Type?:string,kind:'local'|'parameter'}>}
 */
export function getPickerSymbols(host, mode) {
    const locals = (host?.variables || [])
        .filter(v => v && v.Name != null && String(v.Name).trim())
        .map(v => ({
            Name: String(v.Name).trim(),
            Type: v.Type,
            kind: 'local',
        }));

    if (mode !== 'use')
        return locals;

    const params = (host?.parameters || [])
        .filter(p => p && p.Name != null && String(p.Name).trim())
        .map(p => ({
            Name: String(p.Name).trim(),
            Type: p.Type,
            kind: 'parameter',
            Out: !!(p.Out || p.Direction === 1 || p.Direction === 'out'),
        }));

    // Prefer local entry when names collide.
    const localNames = new Set(locals.map(l => l.Name.toLowerCase()));
    const uniqueParams = params.filter(p => !localNames.has(p.Name.toLowerCase()));
    return locals.concat(uniqueParams);
}

/**
 * Insert or replace a local by name. Collapses duplicate names so a create
 * plus its server echo cannot leave a second copy the Variables grid already hid.
 * @param {Array|null|undefined} list
 * @param {{Name?: string}|null|undefined} variable
 * @returns {Array}
 */
export function upsertVariableByName(list, variable) {
    const rows = Array.isArray(list) ? list : [];
    const name = variable && variable.Name != null ? variable.Name : '';
    if (!name)
        return rows;
    for (let i = rows.length - 1; i >= 0; i--) {
        if (rows[i] && rows[i].Name == name)
            rows.splice(i, 1);
    }
    rows.push(variable);
    return rows;
}

/**
 * Remove every local with this name. One delete clears duplicates.
 * @param {Array|null|undefined} list
 * @param {string|null|undefined} name
 */
export function removeVariablesByName(list, name) {
    if (!Array.isArray(list) || name == null || name === '')
        return;
    for (let i = list.length - 1; i >= 0; i--) {
        if (list[i] && list[i].Name == name)
            list.splice(i, 1);
    }
}

/**
 * Build a host-backed context object for provide/inject.
 * @param {{ variables: Array<{Name:string,Type?:string}>, parameters?: Array, addVariable: Function }} host
 * @param {{ enabled?: boolean, defaultType?: string, onUseVariable?: Function }} [options]
 */
export function createVariableBindingContext(host, options = {}) {
    const state = {
        enabled: options.enabled !== false,
        defaultType: options.defaultType || 'string',
        onUseVariable: options.onUseVariable || null,
    };

    return {
        get enabled() {
            return !!state.enabled;
        },
        set enabled(v) {
            state.enabled = !!v;
        },
        get variables() {
            return host.variables || [];
        },
        get parameters() {
            return host.parameters || [];
        },
        getPickerSymbols(mode) {
            return getPickerSymbols(host, mode === 'target' ? 'target' : 'use');
        },
        formatBinding,
        parseBinding,
        isSimpleVariableBinding,
        formatVariableTarget,
        parseVariableTarget,
        isSimpleVariableTarget,
        schemaAllowsVariableBinding,
        schemaAllowsVariableTarget,
        schemaVariableTypeHint,
        normalizeTypeName,
        isStringLikeType,
        typesCompatible,
        /**
         * Register a new local (or return existing). Explicit create only.
         * Refuses names that collide with method parameters.
         * @param {string} name
         * @param {string} [typeHint]
         * @returns {string|null}
         */
        createVariable(name, typeHint) {
            const trimmed = (name == null ? '' : String(name)).trim();
            if (!trimmed)
                return null;

            const existing = (host.variables || []).find(v => v && v.Name === trimmed);
            if (existing)
                return existing.Name;

            const paramHit = (host.parameters || []).find(
                p => p && p.Name && String(p.Name).toLowerCase() === trimmed.toLowerCase(),
            );
            if (paramHit)
                return null;

            const v = {
                Name: trimmed,
                Type: typeHint || state.defaultType || 'string',
                Persistent: false,
            };
            if (!Array.isArray(host.variables))
                host.variables = [];
            host.variables.push(v);
            if (typeof host.addVariable === 'function')
                host.addVariable(v);
            return trimmed;
        },
        notifyUseVariable(payload) {
            if (typeof state.onUseVariable === 'function')
                state.onUseVariable(payload);
        },
        _setEnabled(v) {
            state.enabled = !!v;
        },
    };
}

/**
 * Build pick/create menu options with optional type filtering.
 * @param {Array<{Name:string,Type?:string,kind?:string}>} symbols
 * @param {string} [filterText]
 * @param {{ expectedType?: string, mode?: 'use'|'target' }} [opts]
 * @returns {Array<object>}
 */
export function buildVariablePickerOptions(symbols, filterText, opts = {}) {
    const list = symbols || [];
    const mode = opts.mode === 'target' ? 'target' : 'use';
    const expectedType = opts.expectedType;
    const q = (filterText || '').toLowerCase();
    const typed = (filterText || '').trim();

    const filtered = list.filter((s) => {
        if (!s || s.Name == null)
            return false;
        const name = String(s.Name);
        if (!name)
            return false;
        if (q && !name.toLowerCase().includes(q))
            return false;
        return true;
    });

    const compatible = [];
    const incompatible = [];
    for (const s of filtered) {
        const name = String(s.Name);
        const type = s.Type != null ? String(s.Type) : '';
        const kind = s.kind === 'parameter' ? 'parameter' : 'local';
        const ok = typesCompatible(type, expectedType, { mode });
        const opt = {
            label: name,
            value: name,
            create: false,
            type,
            kind,
            compatible: ok,
        };
        if (ok)
            compatible.push(opt);
        else
            incompatible.push(opt);
    }

    const options = compatible.concat(incompatible);

    const taken = new Set(
        list
            .map(s => (s && s.Name != null ? String(s.Name).toLowerCase() : ''))
            .filter(Boolean),
    );
    if (typed && !taken.has(typed.toLowerCase())) {
        options.unshift({
            label: `Create variable "${typed}"`,
            value: typed,
            create: true,
            compatible: true,
            kind: 'local',
            type: expectedType || '',
        });
    }

    return options;
}

/**
 * Resolve inject value whether it is a plain object or a Vue ref.
 * @param {unknown} injected
 */
export function unwrapVariableBindingContext(injected) {
    if (injected == null)
        return null;
    if (typeof injected === 'object' && 'value' in injected && (injected.__v_isRef || Object.prototype.hasOwnProperty.call(injected, 'value')))
        return injected.value;
    return injected;
}
